import { cn } from "@stockpilot/shared";

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
}

/**
 * Marca Stockpilot: caja/contenedor de bodega isométrico con un check de "stock
 * verificado". El mark vive SIEMPRE dentro de un chip sólido `bg-accent`, y todo
 * el dibujo usa `accent-foreground` — un par de colores cuyo contraste está
 * garantizado por el motor de temas en CADA tema que embarca la app (claro,
 * oscuro y todos los temas de color como Cyber Neon, Synthwave, Royal Velvet…).
 *
 * Gotcha (como GymLog): NUNCA pintar el mark con `text-accent`/`stroke-warning`
 * sobre una superficie tenue — esos tonos desaparecen en algunos temas. El chip
 * sólido + foreground es la única combinación legible universalmente.
 */
function StockpilotMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        "inline-flex items-center justify-center rounded-[28%] bg-accent text-accent-foreground shadow-sm",
        props.class,
      )}
    >
      <svg
        viewBox="0 0 32 32"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="h-[68%] w-[68%]"
        aria-hidden="true"
      >
        {/* Caja / contenedor de bodega (isométrica) */}
        <path
          d="M9 12 16 8.5l7 3.5v8L16 23.5 9 20v-8Z"
          stroke="currentColor"
          stroke-width="1.9"
          stroke-linejoin="round"
          fill="none"
        />
        {/* Aristas internas (volumen de la caja) */}
        <path
          d="M9 12l7 3.5 7-3.5M16 15.5v8"
          stroke="currentColor"
          stroke-width="1.5"
          stroke-linejoin="round"
          opacity="0.55"
        />
        {/* Check de stock verificado */}
        <path
          d="m12.6 15 2.4 2.4 4.4-4.4"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </span>
  );
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";

  return (
    <div
      class={cn(
        "flex items-center gap-3 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <StockpilotMark class="h-8 w-8 shrink-0" />
          <span class="text-xl font-bold tracking-tight text-foreground">
            Stock<span class="text-accent">pilot</span>
          </span>
        </>
      ) : (
        <>
          <StockpilotMark class="mb-2 h-16 w-16 shrink-0 transition-transform duration-300 hover:scale-105 active:scale-95" />
          <h1 class="text-3xl font-bold tracking-tight text-foreground">
            Stock<span class="text-accent">pilot</span>
          </h1>
        </>
      )}
    </div>
  );
}
